import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import {
  Package, CheckCircle, Truck, Clock, XCircle, MapPin, Phone,
  CreditCard, ArrowLeft, Loader2, Search, ShoppingBag
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: '#eab308', bg: 'bg-yellow-50 border-yellow-200' },
  confirmed: { label: 'Confirmed', color: '#2563eb', bg: 'bg-blue-50 border-blue-200' },
  processing: { label: 'Processing', color: '#7c3aed', bg: 'bg-purple-50 border-purple-200' },
  shipped: { label: 'Shipped', color: '#0891b2', bg: 'bg-cyan-50 border-cyan-200' },
  out_for_delivery: { label: 'Out for Delivery', color: '#ea580c', bg: 'bg-orange-50 border-orange-200' },
  delivered: { label: 'Delivered', color: '#16a34a', bg: 'bg-green-50 border-green-200' },
  cancelled: { label: 'Cancelled', color: '#dc2626', bg: 'bg-red-50 border-red-200' }
};

function OrderTrackingPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchId, setSearchId] = useState('');

  const fetchOrder = async (orderId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/track/order/${orderId}`);
      if (res.ok) {
        setOrder(await res.json());
      } else {
        setError('Order not found. Please check your order ID.');
        setOrder(null);
      }
    } catch {
      setError('Unable to fetch order details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchOrder(id);
    else setLoading(false);
  }, [id]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchId.trim()) fetchOrder(searchId.trim());
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const statusCfg = order ? (STATUS_CONFIG[order.status] || STATUS_CONFIG.pending) : STATUS_CONFIG.pending;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="order-tracking-page">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-blue-600 mb-6">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <h1 className="text-2xl font-bold text-gray-900 mb-6">Track Your Order</h1>

        {/* Search Bar */}
        {!id && (
          <form onSubmit={handleSearch} className="flex gap-2 mb-8" data-testid="order-search-form">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                placeholder="Enter Order ID or Order Number"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="order-search-input"
              />
            </div>
            <button type="submit" className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium" data-testid="order-search-btn">
              Track
            </button>
          </form>
        )}

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        )}

        {error && (
          <div className="text-center py-16" data-testid="order-not-found">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">{error}</p>
            {id && (
              <form onSubmit={handleSearch} className="flex gap-2 max-w-md mx-auto">
                <input type="text" value={searchId} onChange={(e) => setSearchId(e.target.value)} placeholder="Try another Order ID" className="flex-1 px-4 py-2 border rounded-lg" />
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg">Track</button>
              </form>
            )}
          </div>
        )}

        {order && !loading && (
          <div className="space-y-6" data-testid="order-tracking-details">
            {/* Order Header */}
            <div className={`border rounded-2xl p-6 ${statusCfg.bg}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-500">Order Number</p>
                  <p className="text-xl font-bold text-gray-900" data-testid="order-number">{order.order_number}</p>
                </div>
                <span className="px-4 py-1.5 rounded-full text-sm font-bold text-white" style={{ backgroundColor: statusCfg.color }} data-testid="order-status-badge">
                  {statusCfg.label}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-2">Placed on {formatDate(order.created_at)}</p>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-2xl border p-6" data-testid="order-timeline">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Order Timeline</h2>
              <div className="relative">
                {order.timeline.map((step, idx) => {
                  const isLast = idx === order.timeline.length - 1;
                  const isCancelled = step.step === 'cancelled';
                  return (
                    <div key={step.step} className="flex gap-4 relative" data-testid={`timeline-step-${step.step}`}>
                      {!isLast && (
                        <div className={`absolute left-[15px] top-8 w-0.5 h-full ${step.completed ? 'bg-blue-200' : 'bg-gray-200'}`} />
                      )}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10 ${
                        isCancelled ? 'bg-red-100' :
                        step.completed ? 'bg-blue-600' : 'bg-gray-200'
                      }`}>
                        {isCancelled ? <XCircle size={16} className="text-red-600" /> :
                         step.completed ? <CheckCircle size={16} className="text-white" /> :
                         <Clock size={16} className="text-gray-400" />}
                      </div>
                      <div className={`pb-6 ${!step.completed && !isCancelled ? 'opacity-40' : ''}`}>
                        <p className={`font-semibold ${isCancelled ? 'text-red-600' : 'text-gray-900'}`}>{step.label}</p>
                        <p className="text-sm text-gray-500">{step.description}</p>
                        {step.timestamp && <p className="text-xs text-gray-400 mt-1">{formatDate(step.timestamp)}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Order Items */}
            <div className="bg-white rounded-2xl border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Items Ordered</h2>
              <div className="divide-y">
                {order.items?.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4 py-3">
                    {item.image && <img src={item.image} alt={item.name} className="w-14 h-14 rounded-lg object-contain bg-gray-50" />}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.name}</p>
                      <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-semibold text-gray-900">Rs.{item.price?.toLocaleString()}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t space-y-1 text-sm">
                {order.subtotal > 0 && <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>Rs.{order.subtotal?.toLocaleString()}</span></div>}
                <div className="flex justify-between text-gray-500"><span>Delivery</span><span>{order.delivery_charge > 0 ? `Rs.${order.delivery_charge}` : 'Free'}</span></div>
                <div className="flex justify-between font-bold text-base text-gray-900 pt-2 border-t"><span>Total</span><span>Rs.{order.total?.toLocaleString()}</span></div>
              </div>
            </div>

            {/* Delivery & Payment */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl border p-6">
                <div className="flex items-center gap-2 mb-3 text-gray-700"><MapPin size={18} /><h3 className="font-semibold">Delivery Address</h3></div>
                <p className="text-gray-900 font-medium">{order.full_name}</p>
                <p className="text-gray-600 text-sm mt-1">{order.address}</p>
                <p className="text-gray-600 text-sm">{order.city}{order.state ? `, ${order.state}` : ''} - {order.pincode}</p>
                {order.phone && <p className="text-gray-500 text-sm mt-1 flex items-center gap-1"><Phone size={14} />{order.phone}</p>}
              </div>
              <div className="bg-white rounded-2xl border p-6">
                <div className="flex items-center gap-2 mb-3 text-gray-700"><CreditCard size={18} /><h3 className="font-semibold">Payment</h3></div>
                <p className="text-gray-900 font-medium capitalize">{order.payment?.payment_method || 'Cash on Delivery'}</p>
                <p className="text-gray-500 text-sm mt-1">Status: <span className="capitalize">{order.payment?.payment_status || 'pending'}</span></p>
                {order.tracking_number && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-700"><Truck size={14} className="inline mr-1" /><strong>Tracking:</strong> {order.tracking_number}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Support */}
            <div className="text-center py-4">
              <p className="text-gray-500 text-sm mb-2">Need help with this order?</p>
              <div className="flex justify-center gap-4">
                <a href="tel:+919887068888" className="text-blue-600 text-sm font-medium hover:underline">Call +91 9887068888</a>
                <a href="https://wa.me/919887068888" target="_blank" rel="noopener noreferrer" className="text-green-600 text-sm font-medium hover:underline">WhatsApp</a>
              </div>
            </div>
          </div>
        )}

        {!id && !loading && !order && !error && (
          <div className="text-center py-16">
            <ShoppingBag className="w-20 h-20 text-gray-200 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Track Your Order</h2>
            <p className="text-gray-500">Enter your order number above to see real-time delivery updates</p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default OrderTrackingPage;
